import { sprinkles } from '@design-system/sprinkles.css';
import { bgColorsVars } from '@design-system/vars.css';
import { Slider, LinearProgress } from '@mui/material';
import { AddFromLibaryVM } from '../../view-models/addFromLibrary.viewModel';
import { Typography } from '@design-system/components/typography/typography';
import { Input } from '@design-system/components/input/input';
import { clsx } from 'clsx';
import { getVideoTimeFormat } from '@client/new/common/videos/videos.helpers';
import { useEffect, useRef, useState } from 'react';
import { IconButton } from '@design-system/components/button/iconButton';
import {
  faClose,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getFileNameWihoutExtension } from '../../../../common/videos/helpers';
import { Button } from '@design-system/components/button/button';
import { Row, Col } from '@design-system/components/grid/grid';
import { useFileUploadViewModel } from '@client/new/shared/file-upload';
import { TemplatesList } from '@client/new/pages/challenges/view/components/TemplatesList';

export const UploadStep = ({
  step,
  progress,
  sliderChange,
  dispatch,
  fileName,
  duration,
  thumbnailTimestamp,
  uploadSizeWarning,
  hasCustomThumbnail,
  ...p
}: {
  step: AddFromLibaryVM['upload']['step'];
  progress: number;
  sliderChange: (_event: Event | null, newValue: number | number[]) => void;
  dispatch: AddFromLibaryVM['dispatch'];
  fileName: string;
  duration: number;
  thumbnailTimestamp: number;
  uploadSizeWarning?: boolean;
  customThumbnailUrl: string;
  hasCustomThumbnail: boolean;
}) => {
  switch (step) {
    case 'Name':
      return <NameEdit fileName={fileName} dispatch={dispatch} />;
    case 'Thumbnail':
      return (
        <Thumbnail
          onCustomThumbnailChange={(url) => dispatch('setCustomThumbnail', url)}
          customThumbnailUrl={p.customThumbnailUrl}
          thumbnailTimestamp={thumbnailTimestamp}
          duration={duration}
          sliderChange={sliderChange}
          hasCustomThumbnail={hasCustomThumbnail}
        />
      );
    case 'Uploading':
      return (
        <UploadProgress
          progress={progress}
          fileName={getFileNameWihoutExtension(fileName)}
          cancelUpload={() => dispatch('cancelUpload')}
          uploadSizeWarning={uploadSizeWarning}
        />
      );
    case 'Processing':
      return <Processing fileName={getFileNameWihoutExtension(fileName)} />;
    default:
      return null;
  }
};

const getFileNameAndExtension = (fileName: string) => {
  const divider = fileName.lastIndexOf('.');
  return [
    fileName.substring(0, divider),
    fileName.substring(divider + 1, fileName.length),
  ];
};

const NameEdit = ({
  dispatch,
  fileName,
}: {
  dispatch: AddFromLibaryVM['dispatch'];
  fileName: string;
}) => {
  const inputEl = useRef<HTMLInputElement>(null);
  const [name, extension] = getFileNameAndExtension(fileName);
  const [localName, setLocalName] = useState(name);

  function handleFocus() {
    if (inputEl.current !== null) {
      inputEl.current.select();
    }
  }

  function handleBlur() {
    dispatch('editName', `${localName}.${extension}`);
  }

  return (
    <Input
      label="Title"
      ref={inputEl}
      required
      value={localName}
      onChange={(ev) => setLocalName(ev.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};

const Thumbnail = ({
  duration,
  sliderChange,
  thumbnailTimestamp,
  hasCustomThumbnail,
  onCustomThumbnailChange,
  customThumbnailUrl,
}: {
  hasCustomThumbnail: boolean;
  duration: number;
  customThumbnailUrl: string;
  sliderChange: (_event: Event | null, newValue: number | number[]) => void;
  thumbnailTimestamp: number;
  onCustomThumbnailChange: (url: string) => void;
}) => {
  const vm = useFileUploadViewModel({
    onChange: onCustomThumbnailChange,
  });

  return (
    <div className="flex flex-col justify-center pt-4">
      <Row alignCenter className="w-full">
        <Typography fontWeight="medium">Thumbnail</Typography>
        {hasCustomThumbnail && (
          <Col>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
              }}
            >
              <input
                type="file"
                accept="image/jpeg,image/png"
                ref={vm.uploadInputRef}
                onChange={(ev) =>
                  ev.target.files &&
                  vm.dispatch('UploadBanner', ev.target.files[0])
                }
                multiple={false}
                hidden
              />
              <Button
                variant="text"
                size="medium"
                color="primary"
                onClick={() => vm.dispatch('StartBannerUpload')}
                loading={vm.isUploading}
              >
                Select from computer
              </Button>
            </div>
          </Col>
        )}
      </Row>
      <Row>
        <Typography variant="body" fontWeight="normal">
          {!customThumbnailUrl
            ? 'Use the slider to select the frame for the thumbnail'
            : null}
        </Typography>
      </Row>

      {customThumbnailUrl ? (
        <TemplatesList
          urls={[customThumbnailUrl]}
          onRemove={() => vm.dispatch('Remove')}
        />
      ) : (
        <>
          <Typography
            variant="body"
            className={sprinkles({ textAlign: 'center' })}
          >
            {getVideoTimeFormat(thumbnailTimestamp)}/
            {getVideoTimeFormat(duration)}
          </Typography>
          <Slider
            value={thumbnailTimestamp}
            max={duration}
            step={0.01}
            onChange={sliderChange}
          />
        </>
      )}
    </div>
  );
};

const UploadProgress = ({
  progress,
  fileName,
  cancelUpload,
  uploadSizeWarning,
}: {
  progress: number;
  fileName: string;
  cancelUpload: () => void;
  uploadSizeWarning?: boolean;
}) => (
  <div className="flex gap-4 items-center justify-between pt-6">
    <div
      className={clsx(
        'w-full',
        sprinkles({
          display: 'flex',
          flexDirection: 'column',
          gap: 'm',
        }),
      )}
    >
      <Typography variant="bodyLarge">{fileName}</Typography>
      <div className="flex w-full gap-4 items-center">
        <div className="w-full mr-1 inline">
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              '&.MuiLinearProgress-root': {
                width: '100%',
                background: bgColorsVars['N-20'],
              },
              '&.MuiLinearProgress-root > .MuiLinearProgress-bar1Determinate': {
                background: bgColorsVars['P-80'],
              },
            }}
          />
        </div>
        <div>
          <Typography
            variant="body"
            className={sprinkles({ color: 'P-80' })}
          >{`${Math.round(progress)}%`}</Typography>
        </div>
      </div>
      {uploadSizeWarning ? (
        <div className="flex gap-1">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className={sprinkles({ color: 'WARNING-900', fontSize: 's' })}
          />
          <Typography
            variant="body"
            size="small"
            className={sprinkles({ color: 'N-60' })}
          >
            Upload takes longer due to file size
          </Typography>
        </div>
      ) : null}
    </div>
    <IconButton
      icon={faClose}
      onClick={cancelUpload}
      variant="text"
      color="neutral"
    />
  </div>
);

const Processing = ({ fileName }: { fileName: string }) => {
  const [warning, setWarning] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setWarning(true), 15000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex flex-col gap-2">
      <Typography variant="bodyLarge">{fileName}</Typography>
      <LinearProgress
        sx={{
          '&.MuiLinearProgress-root': {
            width: '100%',
            background: bgColorsVars['N-20'],
          },
          '&.MuiLinearProgress-root > .MuiLinearProgress-bar1Indeterminate': {
            background: bgColorsVars['P-80'],
          },
        }}
      />
      {warning ? (
        <div className="flex gap-1 pt-2">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className={sprinkles({ color: 'WARNING-900', fontSize: 's' })}
          />
          <Typography
            variant="body"
            size="small"
            className={sprinkles({ color: 'N-60' })}
          >
            Processing is taking longer than usual. This may be due to file
            size, or high demand. Please do not close this window.
          </Typography>
        </div>
      ) : null}
    </div>
  );
};
